import { Component, OnInit, AfterViewInit, NgZone, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortalDirective } from '@angular/cdk/portal';

import { FieldComponent, UndoAction } from './field.component';
import { FormField, Link, Disclaimer } from './form-field.model';
import { NotesDialogComponent } from './notes-dialog.component';
import { DynamicOverlaysDirective } from './dynamic-overlays.directive';

interface FieldValue {
  displayed?: string,
  stored?: string,
  standard?: any
}

@Component({
  selector: 'app-notes-field',
  templateUrl: './notes-field.component.html',
  styleUrls: ['./notes-field.component.scss']
})
export class NotesFieldComponent extends FieldComponent implements OnInit, AfterViewInit {
  @Input() draftData: any;
  @Input() dynamicOverlays: DynamicOverlaysDirective;
  @Input() field: FormField;
  @Input() locked: boolean;
  @Input() lockValues: boolean = false;
  @Input() templateData: any;
  @Input() workflowId: number;
  @Output() focusChanged = new EventEmitter<boolean>();
  @Output() highlightChanged = new EventEmitter<{highlighted: boolean, color?: string}>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() resized = new EventEmitter<FieldComponent>();
  @Output() returnKey = new EventEmitter<FieldComponent>();
  @Output() undoableAction = new EventEmitter<UndoAction>();
  @Output() valueChanged = new EventEmitter<{field: string, value: any}>();
  @Output() viewLoaded = new EventEmitter<FieldComponent>();
  @ViewChild("hintElement") hintElement: ElementRef;
  @ViewChild("hintTemplate") hintTemplate: TemplatePortalDirective;

  id: string;
  defaultValue: string;
  buttonFocused: boolean = false;
  dialogFocused: boolean = false;
  disclaimer: Disclaimer;
  highlightColor: string;
  highlighted: boolean = false;
  hintOverlayRef: OverlayRef;
  previousValue: string;
  value: string;
  versionChanged: boolean = false;

  get focused(): boolean {return this.buttonFocused || this.dialogFocused};
  get touched(): boolean {return false}
  get valid(): boolean {return true}

  constructor(
    cd: ChangeDetectorRef,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private overlay: Overlay) { super(cd) }

  ngOnInit() {
    this.id = this.field.id;
    if (this.locked == null) { this.locked = (this.field.locked === true) }

    this.loadDefaultValue();
    this.loadInitialState(this.draftData);
  }

  ngAfterViewInit() {
    this.viewLoaded.emit(this);
  }

  loadDefaultValue() {
    let templateValue: string;
    if (this.templateData != null) {
      templateValue = this.templateData instanceof Array ? this.templateData[0].stored : this.templateData
    }

    if (templateValue != null) {
      this.defaultValue = templateValue
      if (this.lockValues) { this.locked = true };
    } else if (this.field.defaultValue != null) {
      this.defaultValue = this.field.defaultValue.stored;
    }
  }

  loadInitialState(draftValue?: string) {
    this.value = draftValue != null ? draftValue : this.defaultValue
    this.emitValue();
  }

  emitValue() {
    this.valueChanged.emit({field: this.id, value: this.value});
  }

  setValue(value) {
    this.value = value;
  }

  setPreviousValue(value) {
    this.previousValue = value;
    this.versionChanged = this.value !== this.previousValue;
  }

  resetPreviousValue() {
    this.previousValue = null;
    this.versionChanged = false;
    this.cd.markForCheck();
  }

  positionOverlays() { }

  onFocus(event) { 
    this.buttonFocused = true;
    this.showHint();
    this.focusChanged.emit(this.focused);
    return true;
  }

  onBlur(event) {
    this.buttonFocused = false;
    this.hideHint();
    this.focusChanged.emit(this.focused);
    return true;
  }

  showNotesDialog() {
    this.dialogFocused = true;
    this.focusChanged.emit(this.focused);
    let dialogRef = this.dialog.open(NotesDialogComponent, {
      height: '240px',
      width: '480px',
      data: {
        field: this.field,
        value: this.value,
        locked: this.locked,
        previousValue: this.previousValue,
        versionChanged: this.versionChanged
      }
    });
    dialogRef.afterClosed().subscribe(() => {
      if (!this.locked) {
        this.value = dialogRef.componentInstance.value;
        this.emitValue();
      }
      this.dialogFocused = false;
      this.focusChanged.emit(this.focused);
      this.cd.markForCheck();
    })
    dialogRef.afterOpen().subscribe(() => {
      dialogRef.componentInstance.onOpen();
    })
  }

  showHint() {
    let config = new OverlayConfig();
    config.positionStrategy = this.overlay.position().connectedTo(
      this.hintElement,
      {originX: 'center', originY: 'bottom'},
      {overlayX: 'center', overlayY: 'top'}
    );
    config.scrollStrategy = this.overlay.scrollStrategies.close();
    this.hintOverlayRef = this.overlay.create(config);
    this.hintOverlayRef.attach(this.hintTemplate);
  }

  hideHint() {
    if (this.hintOverlayRef != null) {this.hintOverlayRef.dispose()};
  }

  onKey(event): boolean {
    switch (event.keyCode) {
    case 13:
      return this.onReturnKey();
    default: 
      return true;
    }
  }

  onReturnKey() {
    this.returnKey.emit(this);
    return false;
  }

  compare(other): number { return 0 }

  reset() { }

  setCategories(category) { }

  updateLinkedFields() { }

  setPatternValue(field: string, value: any) { }

  undo(action: UndoAction) { }

  setLockValues(state) { }
}


// WEBPACK FOOTER //
// ./src/app/forms/notes-field.component.ts